import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { JOURNAL } from "../lib/content";

export default function JournalPage() {
  const [featured, ...rest] = JOURNAL;

  return (
    <main data-testid="journal-page" className="pt-28 pb-24 px-5 md:px-10">
      <div className="max-w-[1200px] mx-auto">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember mb-4">The Journal</p>
        <h1 className="font-display font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
          Notes from the <span className="font-serif italic font-normal lowercase text-ember">burrow</span>
        </h1>
        <p className="font-mono text-sm text-cream/75 mt-5 max-w-lg leading-relaxed">
          Roasting science, cocktail mischief and lifestyle dispatches from Nashville. New entries whenever the gremlins finish their coffee.
        </p>

        <Link
          to={`/journal/${featured.slug}`}
          data-testid={`journal-featured-${featured.slug}`}
          className="group block mt-12 border border-white/10 rounded-lg overflow-hidden bg-ash hover:border-ember/60 transition-colors"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative overflow-hidden min-h-[260px]">
              <img src={featured.cover} alt={featured.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-void/60 to-transparent" />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-4">
                <span className="font-mono text-[10px] uppercase tracking-widest bg-ember text-void px-2.5 py-1 rounded-full">{featured.tag}</span>
                <span className="font-mono text-[11px] text-cream/45 flex items-center gap-1.5"><Clock size={12} /> {featured.read}</span>
              </div>
              <h2 className="font-display font-black uppercase tracking-tight text-cream text-3xl md:text-4xl leading-[0.95] mt-5 group-hover:text-ember transition-colors">
                {featured.title}
              </h2>
              <p className="font-mono text-sm text-cream/75 mt-4 leading-relaxed">{featured.excerpt}</p>
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-ember mt-6">
                Read the story <ArrowRight size={13} />
              </span>
            </div>
          </div>
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          {rest.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link
                to={`/journal/${p.slug}`}
                data-testid={`journal-card-${p.slug}`}
                className="group block border border-white/10 rounded-lg overflow-hidden bg-ash hover:border-ember/60 transition-colors h-full"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={p.cover} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-void/50 to-transparent" />
                  <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-ember text-void px-2.5 py-1 rounded-full">{p.tag}</span>
                </div>
                <div className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-cream/55">{p.date} · {p.read}</p>
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-cream text-xl leading-tight mt-3 group-hover:text-ember transition-colors">{p.title}</h3>
                  <p className="font-mono text-xs text-cream/70 mt-3 leading-relaxed">{p.excerpt}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
